// Клиент `/api/v1/connections/ci-logs/*`.
//
// У организации одно подключение к CI-системе, отдельное от репо-хоста
// и трекера задач: токен вводится здесь. Контракт —
// `backend/modules/ci_log/{schemas,router}.py`. PAT никогда не
// возвращается в response. Отказы — `ApiError` (`code`), коды — в
// `docs/spec/modules/ci_log.md`. Образец — `taskTrackerApi.ts`.

import { apiErrorFromResponse } from "../../api/apiError";
import { authedFetch } from "../../auth/authedFetch";
import { buildJsonHeaders } from "./api";

export const CI_LOGS_URL = "/api/v1/connections/ci-logs";

export interface CiLogProvider {
  readonly name: string;
}

export interface CiLogConnectionBody {
  readonly provider: string;
  readonly host_url: string;
  readonly credentials: { readonly pat: string };
}

export interface CiLogConnection {
  readonly connected: boolean;
  readonly provider: string | null;
  readonly host_url: string | null;
  readonly principal_login: string | null;
}

export interface CiLogVerifyResult {
  readonly principal_login: string;
}

// Зеркало `CiLogStatusReason` (`backend/modules/ci_log/schemas.py`).
export type CiLogStatusReason =
  | "host_not_allowed"
  | "credentials_missing"
  | "credentials_rejected"
  | "unreachable"
  | "provider_unknown"
  | "log_api_unsupported";

export interface CiLogStatus {
  readonly connected: boolean;
  readonly reachable: boolean | null;
  readonly log_api_supported: boolean | null;
  readonly principal_login: string | null;
  readonly principal_matches: boolean | null;
  readonly reason: CiLogStatusReason | null;
}

export async function getCiLogProviders(): Promise<readonly CiLogProvider[]> {
  const response = await authedFetch(`${CI_LOGS_URL}/providers`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "getCiLogProviders");
  }
  return (await response.json()) as readonly CiLogProvider[];
}

export async function getCiLogConnection(): Promise<CiLogConnection> {
  const response = await authedFetch(CI_LOGS_URL, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "getCiLogConnection");
  }
  return (await response.json()) as CiLogConnection;
}

export async function getCiLogStatus(): Promise<CiLogStatus> {
  const response = await authedFetch(`${CI_LOGS_URL}/status`, {
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "getCiLogStatus");
  }
  return (await response.json()) as CiLogStatus;
}

/**
 * Проверить креды без сохранения (`POST /verify`).
 *
 * `401 credentials_rejected` — CI-система отвергла токен; сессию не трогает
 * `authedFetch` по коду (ADR-0091 § 7), `401 not_authenticated` — разлогин.
 */
export async function verifyCiLog(
  body: CiLogConnectionBody,
): Promise<CiLogVerifyResult> {
  const response = await authedFetch(`${CI_LOGS_URL}/verify`, {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "verifyCiLog");
  }
  return (await response.json()) as CiLogVerifyResult;
}

/** Подключить CI-систему (проба + сохранение, `PUT /`). */
export async function putCiLogConnection(
  body: CiLogConnectionBody,
): Promise<CiLogConnection> {
  const response = await authedFetch(CI_LOGS_URL, {
    method: "PUT",
    headers: buildJsonHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "putCiLogConnection");
  }
  return (await response.json()) as CiLogConnection;
}

/** Отключить CI-систему (`DELETE /`). */
export async function deleteCiLogConnection(): Promise<void> {
  const response = await authedFetch(CI_LOGS_URL, {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw await apiErrorFromResponse(response, "deleteCiLogConnection");
  }
}
